<script lang="ts" setup>
import type { TaskUser } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { CheckboxGroupRoot } from 'reka-ui'
import { refDebounced } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { faCheck, faMagnifyingGlass, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { UserRole } from '~/models/User/UserRole'
import { TaskStatus } from '~/models/AssignTask'
import useGroupsApi from '~/api/groupsApi'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'

const { currentStudents } = defineProps<{ currentStudents: TaskUser[] }>()
const emit = defineEmits(['addStudents'])

const { t } = useI18n()
const { searchUsers } = useGroupsApi()

const open = ref(false)
const students = ref<TaskUser[]>([])
const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 300)

const { data, isLoading, error } = useQuery({
  queryKey: computed(() => ['searchUsers', debouncedSearch.value]),
  queryFn: () => searchUsers(debouncedSearch.value),
  select: data => data
    .filter(student => student.role === UserRole.Student)
    .map(student => ({
      userId: student.userId,
      status: TaskStatus.Open,
      firstName: student.firstName,
      lastName: student.lastName,
      fullName: student.fullName,
      primaryRole: student.role
    })),
  enabled: computed(() => debouncedSearch.value.length >= 3),
  staleTime: Infinity,
})

const showTip = computed(() => [1, 2].includes(searchQuery.value.length))

const studentIsSelected = (student: TaskUser) => currentStudents.some(({ userId }) => userId === student.userId)

const toggleStudent = (student: TaskUser) => {
  const index = students.value.findIndex(({ userId }) => userId === student.userId)
  return index === -1 ? students.value.push(student) : students.value.splice(index, 1)
}

const handleAddStudents = () => {
  emit('addStudents', students.value)
  searchQuery.value = ''
  students.value = []
  open.value = false
}
</script>

<template>
  <AuDialog
    v-model:open="open"
    :title="t('assign.searchForStudents')"
  >
    <template #trigger>
      <button
        class="text-seagreen-40 hover:text-seagreen-50 mt-6 underline focus-visible:ring"
        @click="open = true"
        v-text="t('assign.missingStudents')"
      />
    </template>
    <template #content>
      <div class="flex flex-col gap-4">
        <div class="relative">
          <div class="text-gray-40 absolute top-1/2 left-5 z-10 flex -translate-y-1/2 justify-center text-base">
            <font-awesome-icon
              :fade="isLoading"
              :icon="faMagnifyingGlass"
            />
          </div>
          <input
            v-model="searchQuery"
            v-focus
            :placeholder="t('assign.searchSchoolPlaceholder')"
            class="placeholder:text-gray-40 border-gray-10 w-full rounded-xl border bg-white p-2 pl-12 text-base transition focus-visible:ring"
            name="search"
            type="text"
          >
          <Button
            v-if="searchQuery.length"
            class="absolute top-1/2 right-1.5 z-10 -translate-y-1/2"
            shape="square"
            size="sm"
            variant="ghost"
            @click="() => {
              searchQuery = ''
              showTip = false
            }"
          >
            <font-awesome-icon :icon="faXmark" size="lg" />
          </Button>
        </div>
        <AuSpinner
          v-if="isLoading"
          class="mt-4"
        />
        <div
          v-else-if="error"
          class="text-coral-40 text-sm"
          v-text="t('assign.error')"
        />
        <div
          v-else-if="searchQuery.length >= 3 && !data?.length"
          class="text-sm"
          v-text="t('assign.noStudents')"
        />
        <div
          v-else-if="showTip"
          class="text-coral-40 text-sm"
          v-text="t('assign.searchForStudentsTip')"
        />
        <ScrollBox
          v-else-if="data?.length"
          class="max-h-72 !overflow-x-hidden"
        >
          <CheckboxGroupRoot v-model="students">
            <transition-group
              class="relative"
              name="list"
              tag="ul"
            >
              <li
                v-for="student in data"
                :key="`student-${student.userId}`"
                :class="{ 'text-green-20 pointer-events-none': studentIsSelected(student) }"
                class="border-green-10 size-full border-b last:border-b-0"
              >
                <label
                  :for="`id-${student.userId}`"
                  class="hover:bg-secondary-hover flex size-full cursor-pointer items-center justify-between px-1 py-3 font-medium transition-all hover:rounded hover:pl-2"
                >
                  <span v-text="student.fullName" />
                  <AuCheckbox
                    v-if="!studentIsSelected(student)"
                    :id="`id-${student.userId}`"
                    :value="student"
                  />
                  <span
                    v-else
                    class="flex items-center gap-2"
                  >
                    <span
                      class="text-xs"
                      v-text="t('assign.studentAdded')"
                    />
                    <font-awesome-icon
                      :icon="faCheck"
                      class="pr-2"
                    />
                  </span>
                </label>
              </li>
            </transition-group>
          </CheckboxGroupRoot>
        </ScrollBox>
        <div
          v-if="students.length"
          class="flex flex-wrap items-center gap-x-2"
        >
          <ul class="flex flex-wrap gap-x-1">
            <li
              v-for="student in students"
              :key="`key-${student.userId}`"
              class="flex items-center text-sm font-medium"
            >
              <span>{{ student.fullName }}</span>
              <Button
                shape="square"
                size="sm"
                variant="ghost"
                @click="toggleStudent(student)"
              >
                <font-awesome-icon :icon="faXmark" />
              </Button>
            </li>
          </ul>
        </div>
        <div class="flex justify-end gap-4">
          <Button
            color="gray"
            shade="light"
            size="lg"
            @click="open = false"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :disabled="!students.length"
            size="lg"
            @click="handleAddStudents"
          >
            {{ t('assign.addStudents') }}
          </Button>
        </div>
      </div>
    </template>
  </AuDialog>
</template>

<style scoped>
.list-move,
.list-enter-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  transition: all 0.2s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.list-leave-active {
  position: absolute;
}
</style>
